import React, { useState, useEffect } from 'react';
import { Form, Input, Upload, message, Spin, Avatar } from 'antd';
import { UserOutlined, CameraOutlined } from '@ant-design/icons';
import { Button } from '../../../design-system';
import { useClientAuth, useClientAuthDispatch } from '../../../context/ClientAuthContext';
import clientAPI from '../../../service/ApiServiceClientPortal';

const BRAND_BLACK = '#1B1B1B';
const BRAND_YELLOW = '#FED230';

const styles = {
    title: {
        fontSize: 22,
        fontWeight: 600,
        color: BRAND_BLACK,
        margin: '0 0 24px 0',
    },
    card: {
        background: '#fff',
        borderRadius: 8,
        padding: 32,
        maxWidth: 600,
    },
    avatarSection: {
        display: 'flex',
        alignItems: 'center',
        gap: 24,
        marginBottom: 32,
    },
    avatarWrapper: {
        position: 'relative',
        cursor: 'pointer',
    },
    avatarOverlay: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        background: BRAND_YELLOW,
        borderRadius: '50%',
        width: 28,
        height: 28,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px solid #fff',
    },
    saveRow: {
        marginTop: 24,
    },
};

const ClientProfile = () => {
    const { contact, jwtToken } = useClientAuth();
    const dispatch = useClientAuthDispatch();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [profilePicture, setProfilePicture] = useState(null);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        const fetchProfile = async () => {
            setLoading(true);
            try {
                const response = await clientAPI.get('/client-portal/profile');
                const data = response.data?.data || response.data;
                const profile = data.profile || data.contact || data;
                form.setFieldsValue({
                    firstName: profile.firstName || '',
                    lastName: profile.lastName || '',
                    title: profile.title || '',
                    phone: profile.phone || '',
                    address: profile.address || '',
                });
                setProfilePicture(profile.profilePicture || null);
            } catch (err) {
                message.error('Failed to load profile');
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, [form]);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);
            const response = await clientAPI.put('/client-portal/profile', values);
            const data = response.data?.data || response.data;
            const updatedContact = data.profile || data.contact || data;

            // Update auth context with new profile data
            if (contact) {
                dispatch({
                    type: 'login',
                    payload: {
                        contactId: contact.id || updatedContact.id,
                        token: jwtToken, // Keep existing token
                        contact: { ...contact, ...updatedContact },
                    },
                });
            }

            message.success('Profile updated successfully');
        } catch (err) {
            if (err.errorFields) return; // Form validation error
            const errMsg = err.response?.data?.data?.message || err.response?.data?.message || 'Failed to update profile';
            message.error(errMsg);
        } finally {
            setSaving(false);
        }
    };

    const handleUpload = async (info) => {
        const file = info.file;
        if (!file) return;

        const formData = new FormData();
        formData.append('profilePicture', file);

        setUploading(true);
        try {
            const response = await clientAPI.post('/client-portal/profile/picture', formData);
            const data = response.data?.data || response.data;
            setProfilePicture(data.profilePicture || data.url || null);
            message.success('Profile picture updated');
        } catch (err) {
            const errMsg = err.response?.data?.data?.message || err.response?.data?.message || 'Failed to upload picture';
            message.error(errMsg);
        } finally {
            setUploading(false);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: 80 }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div>
            <h1 style={styles.title}>My Profile</h1>

            <div style={styles.card}>
                <div style={styles.avatarSection}>
                    <Upload
                        showUploadList={false}
                        beforeUpload={() => false}
                        onChange={handleUpload}
                        accept="image/*"
                    >
                        <div style={styles.avatarWrapper}>
                            <Avatar
                                size={80}
                                icon={<UserOutlined />}
                                src={profilePicture}
                                style={{ backgroundColor: BRAND_YELLOW, color: BRAND_BLACK }}
                            />
                            <div style={styles.avatarOverlay}>
                                {uploading ? (
                                    <Spin size="small" />
                                ) : (
                                    <CameraOutlined style={{ fontSize: 14, color: BRAND_BLACK }} />
                                )}
                            </div>
                        </div>
                    </Upload>
                    <div>
                        <div style={{ fontWeight: 500, color: BRAND_BLACK }}>Profile Picture</div>
                        <div style={{ fontSize: 12, color: '#888' }}>Click to upload a new photo</div>
                    </div>
                </div>

                <Form form={form} layout="vertical">
                    <Form.Item
                        name="firstName"
                        label="First Name"
                        rules={[{ required: true, message: 'First name is required' }]}
                    >
                        <Input placeholder="Enter first name" />
                    </Form.Item>
                    <Form.Item
                        name="lastName"
                        label="Last Name"
                        rules={[{ required: true, message: 'Last name is required' }]}
                    >
                        <Input placeholder="Enter last name" />
                    </Form.Item>
                    <Form.Item name="title" label="Title">
                        <Input placeholder="e.g. Marketing Director" />
                    </Form.Item>
                    <Form.Item name="phone" label="Phone">
                        <Input placeholder="Enter phone number" />
                    </Form.Item>
                    <Form.Item name="address" label="Address">
                        <Input.TextArea rows={2} placeholder="Enter address" />
                    </Form.Item>
                </Form>

                <div style={styles.saveRow}>
                    <Button
                        variant="primary"
                        onClick={handleSave}
                        loading={saving}
                    >
                        Save Changes
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ClientProfile;
